import { useEffect, useMemo, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';

import { hasWithoutCompetitors } from '../components/similar_offers/utils/hasWithoutCompetitors';
import { similarOffersSelector, offerDataSelector } from '../selectors';
import { EModelTypes, requestSimilarOffers } from '../store/similar';

import type { DispatchReduxAction } from '../store';

export function useSimilarOffersGroups(userBasedVisible = true) {
  const dispatch = useDispatch<DispatchReduxAction>();
  const similarOffers = useSelector(similarOffersSelector);
  const offerData = useSelector(offerDataSelector);
  const [offersLoaded, setOfferLoaded] = useState(false);

  const promises = useMemo((): Promise<void>[] => [], []);

  const withoutCompetitors = hasWithoutCompetitors(similarOffers);

  useEffect(() => {
    if (promises.length) {
      Promise.all(promises).then(() => setOfferLoaded(true));
    }
  }, [promises.length, promises]);

  useEffect(() => {
    const {
      offer: { cianId: offerId, publishedUserId },
    } = offerData;

    promises.push(
      dispatch(
        requestSimilarOffers({
          offerId,
          modelType: EModelTypes.HistoryBased,
          publishedUserId,
        }),
      ).catch(e => e),
    );
  }, [dispatch, offerData, promises]);

  useEffect(() => {
    const {
      offer: { cianId: offerId, publishedUserId },
    } = offerData;

    if (userBasedVisible) {
      promises.push(
        dispatch(
          requestSimilarOffers({
            offerId,
            modelType: EModelTypes.UserBased,
            publishedUserId,
          }),
        ).catch(e => e),
      );
    }
  }, [dispatch, userBasedVisible, offerData, promises]);

  useEffect(() => {
    const {
      offer: { cianId: offerId, publishedUserId },
    } = offerData;

    if (!withoutCompetitors) {
      promises.push(
        dispatch(
          requestSimilarOffers({
            offerId,
            modelType: EModelTypes.ItemBased,
            publishedUserId,
          }),
        ).catch(e => e),
      );
      promises.push(
        dispatch(
          requestSimilarOffers({
            offerId,
            modelType: EModelTypes.NewItemBased,
            publishedUserId,
          }),
        ).catch(e => e),
      );
    }
  }, [dispatch, withoutCompetitors, offerData, promises]);

  return useMemo(
    () => ({
      offerData,
      similarOffers,
      withoutCompetitors,
      offersLoaded,
    }),
    [offerData, similarOffers, withoutCompetitors, offersLoaded],
  );
}
